import React from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Bell,
  TrendingUp,
  ArrowRight,
  ShoppingCart,
  Clock,
  BarChart3,
  Package,
  Star,
  Tag,
  Link as LinkIcon,
} from "lucide-react";
import { getSaasUrl } from "../utils/saasUrl";

const MarketplaceMonitoring = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-red-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6">
              <span className="gradient-text">Marketplace</span>
              <br />
              Monitoring
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Track competitor prices across Google Shopping, Amazon, eBay, and
              any marketplaces. Stay ahead with real-time price intelligence and
              automated alerts.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/google-price-monitoring.png"
                alt="Google Price Monitoring Dashboard"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-pink-600/70 rounded-lg flex items-center justify-center bg-white">
                  <Search className="h-6 w-6 text-pink-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Comprehensive Coverage
                  </h3>
                  <p className="text-gray-600">
                    Monitor prices across Google, Amazon, eBay, and any other
                    global marketplaces.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-pink-600/70 rounded-lg flex items-center justify-center bg-white">
                  <Bell className="h-6 w-6 text-pink-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Smart Alerts</h3>
                  <p className="text-gray-600">
                    Get instant notifications when competitors change prices or
                    new competitors appear.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-pink-600/70 rounded-lg flex items-center justify-center bg-white">
                  <TrendingUp className="h-6 w-6 text-pink-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Historical Analysis
                  </h3>
                  <p className="text-gray-600">
                    Track price trends and patterns to predict competitor moves
                    and market changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplaces Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="gradient-text">Supported Marketplaces</span>
            </h2>
            <p className="text-xl text-gray-600">
              Track prices across major platforms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="card">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <img
                    src="/images/logos/google-logo.png"
                    alt="Google Shopping"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold">Google Shopping</h3>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <img
                    src="/images/logos/amazon-logo.png"
                    alt="Amazon"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold">Amazon</h3>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <img
                    src="/images/logos/ebay-logo.png"
                    alt="eBay"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold">eBay</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="gradient-text">Monitoring Features</span>
            </h2>
            <p className="text-xl text-gray-600">
              Advanced tools for competitive intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Real-time Tracking",
                description:
                  "Monitor competitor prices 24/7 with instant updates",
                icon: Clock,
              },
              {
                title: "Price History",
                description:
                  "Complete historical data with trend analysis and forecasting",
                icon: BarChart3,
              },
              {
                title: "Stock Monitoring",
                description:
                  "Track inventory levels and availability across platforms",
                icon: Package,
              },
              {
                title: "Review Analysis",
                description: "Monitor competitor ratings and customer feedback",
                icon: Star,
              },
              {
                title: "Promotion Tracking",
                description:
                  "Identify competitor sales, discounts, and promotional campaigns",
                icon: Tag,
              },
              {
                title: "API Integration",
                description:
                  "Seamlessly integrate data into your existing systems",
                icon: LinkIcon,
              },
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="card">
                  <div className="mb-4">
                    <IconComponent className="h-8 w-8 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Stay Ahead of the{" "}
                <span className="gradient-text">Competition</span>
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      Win the Buy Box
                    </h3>
                    <p className="text-gray-600">
                      Optimize prices to win Amazon's Buy Box and increase sales
                      visibility.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-red-600 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bell className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      Instant Alerts
                    </h3>
                    <p className="text-gray-600">
                      React quickly to competitor price changes with real-time
                      notifications.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      Market Intelligence
                    </h3>
                    <p className="text-gray-600">
                      Gain deep insights into market trends and competitor
                      strategies.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="gradient-bg rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">
                  Start Monitoring Today
                </h3>
                <p className="mb-6">
                  Track your competitors across 1000+ marketplaces and never
                  miss a price change again.
                </p>
                <a
                  href={getSaasUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
                >
                  Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Never Miss a Price Change Again
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Monitor 1000+ marketplaces with real-time alerts and comprehensive
            analytics
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={getSaasUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketplaceMonitoring;
